import { useState, useEffect, useCallback } from 'react';
import { useUrlParams } from '../../../hooks/useUrlParams';
import { alumniService } from '../../../services/alumni.service';
import type { AlumniProfile, AlumniListResponse } from '../../../services/alumni.service';
import { readListCache, writeListCache, clearListCache } from '../../../utils/listCache';

/** Params omitted from the URL when they hold these values, to keep links tidy. */
const PARAM_DEFAULTS: Record<string, string> = {
  page: '1',
  sort_by: 'target_ask',
  sort_order: '-1',
};

export function useAlumni(initialLimit = 50) {
  // The directory's view state lives in the URL rather than in component state, so it
  // survives opening a profile and coming back, a refresh, and being shared as a link.
  const { get, patch: patchParams } = useUrlParams(PARAM_DEFAULTS);

  const searchTerm = get('q');
  const batchFilter = get('batch');
  const departmentFilter = get('department');
  const locationFilter = get('location');
  const industryFilter = get('industry');
  const linkedinStatusFilter = get('linkedin_status');
  const researchLockedFilter = get('research_locked');
  const page = Number(get('page')) || 1;
  const sortBy = get('sort_by');
  const sortOrder = Number(get('sort_order')) === 1 ? 1 : -1;

  const query = {
    page,
    limit: initialLimit,
    q: searchTerm.trim() || undefined,
    batch: batchFilter.trim() || undefined,
    department: departmentFilter.trim() || undefined,
    location: locationFilter.trim() || undefined,
    industry: industryFilter.trim() || undefined,
    linkedin_status: linkedinStatusFilter.trim() || undefined,
    research_locked: researchLockedFilter === 'true' ? true : researchLockedFilter === 'false' ? false : undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  };
  const cacheKey = `alumni-list:${JSON.stringify(query)}`;

  const [fetched, setFetched] = useState<{ key: string; data: AlumniListResponse } | null>(null);
  const [failure, setFailure] = useState<{ key: string; message: string } | null>(null);
  const [selectedAlumni, setSelectedAlumni] = useState<AlumniProfile | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  // What we render is derived, not copied into state: whatever this query last returned,
  // else whatever it returned on a previous visit. That way coming back from a profile
  // paints the rows the user was reading on the very first render, with no skeleton.
  const data = fetched?.key === cacheKey ? fetched.data : readListCache<AlumniListResponse>(cacheKey);
  const error = failure?.key === cacheKey ? failure.message : null;

  const alumniList = data?.alumni ?? [];
  const totalPages = data?.pages ?? 1;
  const totalItems = data?.total ?? 0;
  const loading = !data && !error;

  /**
   * Force a fresh read after the user changes the underlying data. Needed because
   * bouncing the page number can't do it — resetting to page 1 while already on page 1
   * leaves the URL untouched, so nothing re-runs.
   */
  const refresh = useCallback(() => {
    clearListCache('alumni-list:');
    setFetched(null);
    setRefreshNonce(n => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const hadRows = readListCache<AlumniListResponse>(cacheKey) !== undefined;

    const fetchAlumni = async () => {
      try {
        const result = await alumniService.getAlumniList(query);
        writeListCache(cacheKey, result);
        if (!cancelled) setFetched({ key: cacheKey, data: result });
      } catch (err) {
        if (cancelled) return;
        // A failed background refresh shouldn't replace good rows with an error —
        // keep showing what the user had and let the next attempt recover.
        if (hadRows) console.error('Background alumni refresh failed:', err);
        else setFailure({
          key: cacheKey,
          message: err instanceof Error ? err.message : 'An error occurred while fetching alumni profiles.',
        });
      }
    };

    // The debounce is there to swallow keystrokes. With rows already on screen there is
    // nothing to swallow, so revalidate immediately.
    const debounceTimer = setTimeout(fetchAlumni, hadRows ? 0 : 300);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
    };
    // `query` is rebuilt every render; cacheKey is its stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, refreshNonce]);

  const setPage = useCallback((p: number) => patchParams({ page: p }), [patchParams]);
  const setBatchFilter = useCallback((v: string) => patchParams({ batch: v }), [patchParams]);
  const setDepartmentFilter = useCallback((v: string) => patchParams({ department: v }), [patchParams]);
  const setLocationFilter = useCallback((v: string) => patchParams({ location: v }), [patchParams]);
  const setIndustryFilter = useCallback((v: string) => patchParams({ industry: v }), [patchParams]);

  const handleSearchChange = (term: string) => {
    patchParams({ q: term, page: 1 });
  };

  const handleSort = (field: string) => {
    const nextOrder = sortBy === field && sortOrder === -1 ? 1 : -1;
    patchParams({ sort_by: field, sort_order: nextOrder, page: 1 });
  };

  return {
    searchTerm,
    batchFilter,
    setBatchFilter,
    departmentFilter,
    setDepartmentFilter,
    locationFilter,
    setLocationFilter,
    industryFilter,
    setIndustryFilter,
    linkedinStatusFilter,
    setLinkedinStatusFilter: (v: string) => patchParams({ linkedin_status: v, page: 1 }),
    researchLockedFilter,
    setResearchLockedFilter: (v: string) => patchParams({ research_locked: v, page: 1 }),
    alumniList,
    loading,
    error,
    selectedAlumni,
    setSelectedAlumni,
    page,
    setPage,
    totalPages,
    totalItems,
    sortBy,
    sortOrder,
    handleSearchChange,
    handleSort,
    refresh,
  };
}
